import React, { useEffect, useState, useMemo } from "react"
import { Table, Row, Col, Container, Modal, Form, Label, Card, CardBody } from "reactstrap"
import { useNavigate } from "react-router-dom"
import { useTable, usePagination, useGlobalFilter } from "react-table"
import Swal from "sweetalert2"
import NewPagination from "components/Common/Newpagination"
import Breadcrumbs from "components/Common/Breadcrumb"
import Loader from "pages/Separate/Loader"
import { fetchNodeData, FetchNodeDeleteData, FetchNodePostData, FetchNodePutData } from "utils/common-fetchData"
import secureLocalStorage from "react-secure-storage"

// ─── Seed Data ─────────────────────────────────────────────────────────────────
const SEED_DATA = [
    { id: 1, name: "Full-Time" },
    { id: 2, name: "Intern" },
    { id: 3, name: "Contractual" },
]

// ─── Modal ─────────────────────────────────────────────────────────────────────
function EmployeeTypeModal({ isOpen, onClose, value, onChange, onSubmit, isEdit }) {
    return (
        <Modal isOpen={isOpen} size="sm" centered>
            <div className="modal-header">
                <h5 className="modal-title mt-0">
                    {isEdit ? "Edit" : "Add"} Employee Type
                </h5>
                <button type="button" onClick={onClose} className="btn-modal-close">
                    <span className="btn-modal-span">&times;</span>
                </button>
            </div>

            <div className="modal-body">
                <Form onSubmit={onSubmit}>
                    <div className="mb-3">
                        <Label>
                            Employee Type <span className="text-danger">*</span>
                        </Label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. Full-Time, Intern"
                            value={value}
                            onChange={e => onChange(e.target.value)}
                            required
                            autoFocus
                        />
                    </div>

                    <div className="mt-3 d-flex gap-2">
                        <button type="submit" className="border-radius btn btn-purple">
                            <i className="fas fa-save me-2"></i>
                            {isEdit ? "Update" : "Submit"}
                        </button>
                    </div>
                </Form>
            </div>
        </Modal>
    )
}

// ─── Table Container ───────────────────────────────────────────────────────────
function TableContainer({ columns, data, onAdd }) {
    const [fixedContent, setFixedContent] = useState(false)
    const navigate = useNavigate()

    useEffect(() => {
        const handleScroll = () => setFixedContent(window.scrollY > 160)
        document.addEventListener("scroll", handleScroll)
        return () => document.removeEventListener("scroll", handleScroll)
    }, [])

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        state,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        gotoPage,
        setPageSize,
        pageOptions,
        setGlobalFilter,
        prepareRow,
    } = useTable({ columns, data }, useGlobalFilter, usePagination)

    const { pageIndex, pageSize, globalFilter } = state

    return (
        <div className="px-2">
            {/* Floating buttons */}
            <div className={`flex-column fixed-buttons ${fixedContent ? "d-flex" : "d-none"}`}>
                <button onClick={onAdd} className="btn btn-purple border-radius fix-btn">
                    <i className="fas fa-plus me-2"></i>
                    <span>Type</span>
                </button>
                <button onClick={() => navigate(-1)} className="btn btn-red border-radius fix-btn">
                    <i className="dripicons-cross me-2 icon-center"></i>
                    <span>Cancel</span>
                </button>
            </div>

            {/* Toolbar */}
            <Row className="mb-2 align-items-center">
                <Col lg={2}>
                    <select
                        className="border-radius form-select"
                        style={{ width: "100px" }}
                        value={pageSize}
                        onChange={e => setPageSize(Number(e.target.value))}
                    >
                        {[10, 20, 30, 40, 50].map(s => (
                            <option key={s} value={s}>Show {s}</option>
                        ))}
                    </select>
                </Col>

                <Col className="d-flex justify-content-end gap-3 align-items-center">
                    <div className="search-box d-inline-block">
                        <div className="position-relative">
                            <input
                                type="text"
                                className="border-radius form-control rounded"
                                placeholder="Search..."
                                value={globalFilter || ""}
                                onChange={e => setGlobalFilter(e.target.value)}
                            />
                            <i className="bx bx-search-alt search-icon"></i>
                        </div>
                    </div>
                    <button className="border-radius btn btn-purple" onClick={onAdd}>
                        <i className="fas fa-plus me-2 icon-size"></i>Employee Type
                    </button>
                    <button
                        className="border-radius btn btn-red text-white"
                        onClick={() => navigate(-1)}
                    >
                        <i className="dripicons-cross me-2 icon-center"></i>Cancel
                    </button>
                </Col>
            </Row>

            {/* Table */}
            <Table className="table table-bordered mt-4" {...getTableProps()}>
                <thead className={fixedContent ? "fixed-setting-header" : ""}>
                    {headerGroups.map((hg, i) => (
                        <tr key={i} {...hg.getHeaderGroupProps()}>
                            {hg.headers.map((col, j) => (
                                <th key={j} className={col.className} {...col.getHeaderProps()}>
                                    {col.render("Header")}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                        prepareRow(row)
                        return (
                            <tr className="row-hover" key={i} {...row.getRowProps()}>
                                {row.cells.map((cell, j) => (
                                    <td key={j} className={cell.column.className} {...cell.getCellProps()}>
                                        {cell.render("Cell")}
                                    </td>
                                ))}
                            </tr>
                        )
                    })}
                    {page.length === 0 && (
                        <tr>
                            <td colSpan={columns.length} className="text-center text-muted py-4">
                                No employee types found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {/* Pagination */}
            <Row className="justify-content-md-end justify-content-center align-items-center">
                <NewPagination
                    gotoPage={gotoPage}
                    canPreviousPage={canPreviousPage}
                    previousPage={previousPage}
                    canNextPage={canNextPage}
                    pageIndex={pageIndex}
                    pageOptions={pageOptions}
                    pageSize={pageSize}
                    page={page}
                    data={data}
                    nextPage={nextPage}
                />
            </Row>
        </div>
    )
}

// ─── Main Component ────────────────────────────────────────────────────────────
const EmployeeTypeMaster = () => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)

    const [modalOpen, setModalOpen] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const [editId, setEditId] = useState(null)
    const [typeName, setTypeName] = useState("")

    useEffect(() => {
        const fetchTypes = async () => {
            console.log(secureLocalStorage.getItem("auth_token"))
            try {
                const response = await fetchNodeData(setLoading, 'employeeType/get', secureLocalStorage.getItem("auth_token"))
                setData(response.data || [])
            } catch (err) {
                console.log(err)
            }
        }
        fetchTypes()
        document.title = "Employee Types | Zithas Crm"
    }, [])

    // ── Handlers ────────────────────────────────────────────────────────────────
    const openAdd = () => {
        setIsEdit(false); setEditId(null); setTypeName(""); setModalOpen(true)
    }

    const openEdit = row => {
        setIsEdit(true); setEditId(row.id); setTypeName(row.employeetype); setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false); setTypeName(""); setEditId(null)
    }

    const handleSubmit = e => {
        e.preventDefault()

        const token = secureLocalStorage.getItem("auth_token");

        Swal.fire({
            icon: "warning",
            title: "Are you sure ?",
            text: `You want to ${isEdit ? 'update' : 'add'} employee type`,
            showCancelButton: true,
        }).then(async willOkay => {
            if (willOkay.isConfirmed) {
                try {
                    const entry = {
                        employeetype: typeName,
                    }

                    let response;

                    if (isEdit) {

                        // Update existing
                        response = await FetchNodePutData(setLoading, `employeeType/update`, editId, entry)

                        if (response.data.success) {
                            setData(prev => prev.map(item => (item.id === editId ? response.data.data : item)))
                            Swal.fire("Updated!", "Employee type updated successfully.", "success")
                        }
                    } else {

                        // Add new
                        response = await FetchNodePostData(setLoading, `employeeType/add`, token, entry)

                        if (response.data.success) {
                            setData(prev => [response.data.data, ...prev])
                            Swal.fire("Added!", "Employee type added successfully.", "success")
                        }
                    }

                    closeModal()
                } catch (error) {
                    console.log(error)
                    swal("", "Something went wrong!", "error")
                }
            }
        })

    }

    const handleDelete = id => {
        Swal.fire({
            title: "Are you sure?",
            text: "This employee type will be permanently deleted.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#762c99",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then(async r => {
            if (r.isConfirmed) {
                const response = await FetchNodeDeleteData(
                    setLoading,
                    `employeeType/delete`,
                    id
                );

                if (response.data.success) {
                    setData(prev => prev.filter(item => item.id != id))
                    Swal.fire("Deleted!", "Employee type has been deleted.", "success")
                }
            }
        })
    }

    // ── Columns ──────────────────────────────────────────────────────────────────
    const columns = useMemo(() => [
        {
            Header: "ID",
            accessor: "id",
            className: "text-center",
            Cell: ({ row }) => row.index + 1,
        },
        {
            Header: "Employee Type",
            accessor: "employeetype",
            className: "col-9 text-start",
        },
        {
            Header: "Action",
            accessor: "action",
            className: "text-center",
            Cell: ({ row }) => (
                <div className="d-flex justify-content-center gap-1">
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        title="Edit"
                        onClick={() => openEdit(row.original)}
                    >
                        <i className="fas fa-pencil-alt"></i>
                    </button>
                    <button
                        className="btn btn-sm btn-danger"
                        title="Delete"
                        onClick={() => handleDelete(row.original.id)}
                    >
                        <i className="fas fa-trash-alt"></i>
                    </button>
                </div>
            ),
        },
    ], [])

    // ── Render ───────────────────────────────────────────────────────────────────
    return (
        <React.Fragment>
            {loading && <Loader />}

            <div className="page-content mb-4">
                <Container fluid>
                    <Breadcrumbs title="Masters" breadcrumbItem="Employee Types" />

                    <Card>
                        <CardBody>
                            <div className="responsive-table">
                                <TableContainer
                                    columns={columns}
                                    data={data}
                                    onAdd={openAdd}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Container>
            </div>

            <EmployeeTypeModal
                isOpen={modalOpen}
                onClose={closeModal}
                value={typeName}
                onChange={setTypeName}
                onSubmit={handleSubmit}
                isEdit={isEdit}
            />
        </React.Fragment>
    )
}

export default EmployeeTypeMaster
